import type { Product, ConcernScores, SkinType as ProductSkinType } from '@/types/product'
import type { SkinType as SurveySkinType } from '@/types/survey'

// Map analysis metric names to product ConcernScores fields
const METRIC_TO_CONCERN_FIELD: Record<string, keyof ConcernScores> = {
  pores: 'Pore_Care',
  texture: 'Texture_Smoothing',
  wrinkles: 'Anti_Wrinkle',
  redness: 'Redness_Sensitivity',
  acne: 'Acne_Care',
  oiliness: 'Oil_Control',
  pigmentation: 'Pigmentation',
  spots: 'Pigmentation',
  firmness: 'Firmness',
  radiance: 'Tone_Dullness',
  brightness: 'Tone_Dullness',
  moisture: 'Hydration',
  darkCircles: 'Eye_Care',
  eyeBags: 'Eye_Care',
}

// Display names matching analysis metric names (user-facing)
const METRIC_DISPLAY_NAMES: Record<string, string> = {
  pores: 'Pores',
  texture: 'Texture',
  wrinkles: 'Wrinkles',
  redness: 'Redness',
  acne: 'Acne',
  oiliness: 'Oiliness',
  pigmentation: 'Dark Spots',
  spots: 'Dark Spots',
  firmness: 'Firmness',
  radiance: 'Radiance',
  brightness: 'Radiance',
  moisture: 'Moisture',
  darkCircles: 'Dark Circles',
  eyeBags: 'Eye Bags',
}

// Skin type display names
const SKIN_TYPE_DISPLAY: Record<string, string> = {
  oily: 'Oily',
  dry: 'Dry',
  combination: 'Combo',
  normal: 'Normal',
  all: 'All Skin',
  'not-sure': '',
}

// Color coding for different concerns (background color classes)
export const CONCERN_COLORS: Record<string, { bg: string; text: string }> = {
  pores: { bg: 'bg-blue-100', text: 'text-blue-700' },
  texture: { bg: 'bg-purple-100', text: 'text-purple-700' },
  wrinkles: { bg: 'bg-amber-100', text: 'text-amber-700' },
  redness: { bg: 'bg-rose-100', text: 'text-rose-700' },
  acne: { bg: 'bg-red-100', text: 'text-red-700' },
  oiliness: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  pigmentation: { bg: 'bg-orange-100', text: 'text-orange-700' },
  spots: { bg: 'bg-orange-100', text: 'text-orange-700' },
  firmness: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  radiance: { bg: 'bg-pink-100', text: 'text-pink-700' },
  brightness: { bg: 'bg-pink-100', text: 'text-pink-700' },
  moisture: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  darkCircles: { bg: 'bg-violet-100', text: 'text-violet-700' },
  eyeBags: { bg: 'bg-violet-100', text: 'text-violet-700' },
}

export interface ProductTag {
  label: string
  type: 'concern' | 'skinType'
  concernKey?: string // For color coding
}

// User's concern scores from analysis (metric name -> score 0-100)
export type UserConcerns = Record<string, number>

/**
 * Get personalized hashtags showing which of the USER's concerns this product addresses.
 * Only shows concerns where: user has the concern AND product addresses it.
 *
 * @param product - The product to get tags for
 * @param userConcerns - User's concern scores from analysis (e.g., { pores: 65, texture: 45 })
 *                       Higher score = better skin, lower score = needs attention
 * @param userSkinType - Optional user's skin type
 * @param userConcernThreshold - Max user score to consider it a concern (default: 70)
 *                               Scores at or below this indicate the user needs help in this area
 * @param productScoreThreshold - Min product score to consider it effective (default: 3)
 * @param maxTags - Maximum number of tags to return (default: 4)
 */
export function getProductTags(
  product: Product,
  userConcerns?: UserConcerns | null,
  userSkinType?: SurveySkinType | null,
  userConcernThreshold = 70,
  productScoreThreshold = 3,
  maxTags = 4
): ProductTag[] {
  const tags: ProductTag[] = []

  // 1. Get personalized concern tags
  // Only show concerns where user needs help (low score) AND product addresses it
  if (userConcerns) {
    const matchedConcerns: { metric: string; userScore: number; productScore: number }[] = []

    for (const [metric, userScore] of Object.entries(userConcerns)) {
      // Lower score = needs more attention (higher score = better skin)
      if (userScore <= userConcernThreshold) {
        const concernField = METRIC_TO_CONCERN_FIELD[metric]
        if (concernField) {
          const productScore = product.concernScores[concernField]
          if (productScore >= productScoreThreshold) {
            matchedConcerns.push({ metric, userScore, productScore })
          }
        }
      }
    }

    // Sort by user concern severity (lowest score first = worst concern)
    matchedConcerns.sort((a, b) => a.userScore - b.userScore)

    for (const { metric } of matchedConcerns.slice(0, maxTags - 1)) {
      const displayName = METRIC_DISPLAY_NAMES[metric]
      if (displayName) {
        tags.push({ label: displayName, type: 'concern', concernKey: metric })
      }
    }
  }

  // 2. Add skin type tag if user's skin type matches
  const mappedUserType = userSkinType && userSkinType !== 'not-sure' ? userSkinType as ProductSkinType : null

  if (mappedUserType && product.skinTypes.includes(mappedUserType)) {
    const displayName = SKIN_TYPE_DISPLAY[mappedUserType]
    if (displayName) {
      tags.unshift({ label: displayName, type: 'skinType' })
    }
  }

  return tags.slice(0, maxTags)
}

/**
 * Format tags as hashtag strings (e.g., "#Acne #Oily")
 */
export function formatTagsAsHashtags(tags: ProductTag[]): string {
  return tags.map(t => `#${t.label}`).join(' ')
}
